import { AdminCompanySearchResponseDto, AdminCompanyDto, ProfileSearchResponseDto, ProfileDto, AdminCompanyDetailDto } from '@inosio/payments-sdk';
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

interface IProfileDataPage {
  Rows: Array<ProfileDto>;
}
interface IAdminCompanyDataPage {
  Rows: Array<AdminCompanyDto>;
}

type AdminState = {
  ProfileHistory: ProfileSearchResponseDto;
  ProfileDataPages: Array<IProfileDataPage>;
  isProfilesFetching: boolean;
  isProfilesFetchingSuccess: boolean;
  isProfilesFetchingErrror: boolean;
  profilesError: string | null;
  CompanyHistory: AdminCompanySearchResponseDto;
  CompanyDataPages: Array<IAdminCompanyDataPage>;
  isCompaniesFetching: boolean;
  isCompaniesFechingSuccess: boolean;
  isCompaniesFetchingErrror: boolean;
  companiesError: string | null;
  CompanyDetail: AdminCompanyDetailDto;
  isCompanyDetailFetching: boolean;
  isCompanyDetailSuccess: boolean;
  isCompanyDetailErrror: boolean;
  companyDetailError: string | null;
  isExportingBankFiles: boolean;
  isExportBankFilesSuccess: boolean;
  isExportBankFilesFailed: boolean;
  exportApplicationDetailError: string | null;
  isUpdatingCompanyProductConfigs: boolean;
  isUpdatingCompanyProductConfigsSuccess: boolean;
  isUpdatingCompanyProductConfigsFailed: boolean;
  updatingCompanyProductConfigsError: string | null;
  isSubmittingOnboardMerchant: boolean;
  isSubmittingOnboardMerchantSuccess: boolean;
  isSubmittingOnboardMerchantFailed: boolean;

  //export company docs
  isExportingCompanyPciDoc: boolean;
  isExportingCompanyPciDocSuccess: boolean;
  isExportingCompanyPciDocFailed: boolean;
  exportCompanyPciDocError: string;

  //view company docs
  isViewingCompanyPciDoc: boolean;
  isViewingCompanyPciDocSuccess: boolean;
  isViewingCompanyPciDocFailed: boolean;
  viewCompanyPciDocError: string;
};

const getInitialState = () =>
  ({
    ProfileHistory: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    ProfileDataPages: [],
    isProfilesFetching: false,
    isProfilesFetchingSuccess: false,
    isProfilesFetchingErrror: false,
    profilesError: null,
    CompanyHistory: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    CompanyDataPages: [],
    isCompaniesFetching: false,
    isCompaniesFechingSuccess: false,
    isCompaniesFetchingErrror: false,
    companiesError: null,
    CompanyDetail: {} as AdminCompanyDetailDto,
    isCompanyDetailFetching: false,
    isCompanyDetailSuccess: false,
    isCompanyDetailErrror: false,
    companyDetailError: null,
    isExportingBankFiles: false,
    isExportBankFilesSuccess: false,
    isExportBankFilesFailed: false,
    exportApplicationDetailError: null,
    isUpdatingCompanyProductConfigs: false,
    isUpdatingCompanyProductConfigsSuccess: false,
    isUpdatingCompanyProductConfigsFailed: false,
    updatingCompanyProductConfigsError: null,
    isSubmittingOnboardMerchant: false,
    isSubmittingOnboardMerchantSuccess: false,
    isSubmittingOnboardMerchantFailed: false,
    isExportingCompanyPciDoc: false,
    isExportingCompanyPciDocSuccess: false,
    isExportingCompanyPciDocFailed: false,
    exportCompanyPciDocError: '',
    isViewingCompanyPciDoc: false,
    isViewingCompanyPciDocSuccess: false,
    isViewingCompanyPciDocFailed: false,
    viewCompanyPciDocError: '',
  } as AdminState);

const adminSlice = createSlice({
  name: 'portal',
  initialState: getInitialState(),
  reducers: {
    getProfiles(state, action) {
      state.isProfilesFetching = true;
      state.isProfilesFetchingSuccess = false;
      state.isProfilesFetchingErrror = false;
      state.profilesError = null;
    },
    setProfiles(state, action) {
      /*
      this function has one responsiblity: receive updates from API
      and put the results in state.
      */
      let _payload = action.payload as ProfileSearchResponseDto;

      //get a copy of the current book
      let currentPages = [...state.ProfileDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.RecordStart === 0) currentPages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil((_payload.RecordStart || 0) / (_payload.RangeSize || 1));

      //now, add this new data page to our book
      currentPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows ?? [],
      };

      state.ProfileHistory = action.payload;
      state.ProfileDataPages = currentPages;
      state.isProfilesFetching = false;
    },
    setProfilesError(state, action) {
      state.isProfilesFetching = false;
      state.isProfilesFetchingSuccess = false;
      state.isProfilesFetchingErrror = true;
      state.profilesError = action.payload;
    },
    getCompanies(state, action) {
      state.isCompaniesFetching = true;
      state.isCompaniesFechingSuccess = false;
      state.isCompaniesFetchingErrror = false;
      state.companiesError = null;
    },
    setCompanies(state, action) {
      /*
      this function has one responsiblity: receive updates from API
      and put the results in state.
      */
      let _payload = action.payload as AdminCompanySearchResponseDto;

      //get a copy of the current book
      let currentPages = [...state.CompanyDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.RecordStart === 0) currentPages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil((_payload.RecordStart || 0) / (_payload.RangeSize || 1));

      //now, add this new data page to our book
      currentPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows ?? [],
      };

      //return the full
      let fullReturnData = {
        CompanyHistory: action.payload,
        CompanyDataPages: currentPages,
        isCompaniesFetching: false,
        isCompaniesuccess: true,
        isCompaniesErrror: false,
      };

      return { ...state, ...fullReturnData };
    },
    setCompaniesError(state, action) {
      state.isCompaniesFetching = false;
      state.isCompaniesFechingSuccess = false;
      state.isCompaniesFetchingErrror = true;
      state.companiesError = action.payload;
    },
    getCompanyDetail(state, action) {
      state.isCompanyDetailFetching = true;
      state.isCompanyDetailSuccess = false;
      state.isCompanyDetailErrror = false;
      state.companyDetailError = null;
    },
    setCompanyDetail(state, action) {
      state.isCompanyDetailFetching = false;
      state.isCompanyDetailSuccess = true;
      state.isCompanyDetailErrror = false;
      state.CompanyDetail = action.payload;
    },
    setCompanyDetailError(state, action) {
      state.isCompanyDetailFetching = false;
      state.isCompanyDetailSuccess = false;
      state.isCompanyDetailErrror = true;
      state.companyDetailError = action.payload;
    },
    clearCompanyDetail(state) {
      state.isCompanyDetailFetching = false;
      state.isCompanyDetailSuccess = false;
      state.isCompanyDetailErrror = false;
      state.CompanyDetail = {} as AdminCompanyDetailDto;
      state.companyDetailError = null;
    },
    getExportBankFiles(state, action) {
      state.isExportingBankFiles = true;
      state.isExportBankFilesSuccess = false;
      state.isExportBankFilesFailed = false;
      state.exportApplicationDetailError = null;
    },
    setExportBankFiles(state) {
      state.isExportingBankFiles = false;
      state.isExportBankFilesSuccess = true;
      state.isExportBankFilesFailed = false;
      state.exportApplicationDetailError = null;
    },
    setExportBankFilesError(state, action) {
      state.isExportingBankFiles = false;
      state.isExportBankFilesSuccess = false;
      state.isExportBankFilesFailed = true;
      state.exportApplicationDetailError = action.payload.message;
    },
    getUpdateCompanyProductConfigs(state, action) {
      state.isUpdatingCompanyProductConfigs = true;
      state.isUpdatingCompanyProductConfigsSuccess = false;
      state.isUpdatingCompanyProductConfigsFailed = false;
      state.updatingCompanyProductConfigsError = null;
    },
    setUpdateCompanyProductConfigs(state, action) {
      state.isUpdatingCompanyProductConfigs = false;
      state.isUpdatingCompanyProductConfigsSuccess = true;
      state.isUpdatingCompanyProductConfigsFailed = false;
      state.updatingCompanyProductConfigsError = null;
    },
    setUpdateCompanyProductConfigsError(state, action) {
      state.isUpdatingCompanyProductConfigs = false;
      state.isUpdatingCompanyProductConfigsSuccess = false;
      state.isUpdatingCompanyProductConfigsFailed = true;
      state.updatingCompanyProductConfigsError = action.payload.message;
    },
    submitOnboardMerchant(state, action) {
      state.isSubmittingOnboardMerchant = true;
      state.isSubmittingOnboardMerchantSuccess = false;
      state.isSubmittingOnboardMerchantFailed = false;
    },
    setOnboardMerchantData(state, action) {
      state.isSubmittingOnboardMerchant = false;
      state.isSubmittingOnboardMerchantSuccess = true;
      state.isSubmittingOnboardMerchantFailed = false;
    },
    setOnboardMerchantError(state, action) {
      state.isSubmittingOnboardMerchant = false;
      state.isSubmittingOnboardMerchantSuccess = false;
      state.isSubmittingOnboardMerchantFailed = true;
    },
    clearAdminCompanyFlags(state) {
      state.isSubmittingOnboardMerchant = false;
      state.isSubmittingOnboardMerchantSuccess = false;
      state.isSubmittingOnboardMerchantFailed = false;
      state.isUpdatingCompanyProductConfigs = false;
      state.isUpdatingCompanyProductConfigsSuccess = false;
      state.isUpdatingCompanyProductConfigsFailed = false;
    },
    exportCompanyPciDoc(state) {
      state.isExportingCompanyPciDoc = true;
      state.isExportingCompanyPciDocSuccess = false;
      state.isExportingCompanyPciDocFailed = false;
      state.exportCompanyPciDocError = '';
    },
    setExportCompanyPciDoc(state) {
      state.isExportingCompanyPciDoc = false;
      state.isExportingCompanyPciDocSuccess = true;
      state.isExportingCompanyPciDocFailed = false;
      state.exportCompanyPciDocError = '';
    },
    setExportCompanyPciDocError(state, action) {
      state.isExportingCompanyPciDoc = false;
      state.isExportingCompanyPciDocSuccess = false;
      state.isExportingCompanyPciDocFailed = true;
      state.exportCompanyPciDocError = action.payload.message;
    },
    viewCompanyPciDoc(state) {
      state.isViewingCompanyPciDoc = true;
      state.isViewingCompanyPciDocSuccess = false;
      state.isViewingCompanyPciDocFailed = false;
      state.viewCompanyPciDocError = '';
    },
    setViewCompanyPciDoc(state) {
      state.isViewingCompanyPciDoc = false;
      state.isViewingCompanyPciDocSuccess = true;
      state.isViewingCompanyPciDocFailed = false;
      state.viewCompanyPciDocError = '';
    },
    setViewCompanyPciDocError(state, action) {
      state.isViewingCompanyPciDoc = false;
      state.isViewingCompanyPciDocSuccess = false;
      state.isViewingCompanyPciDocFailed = true;
      state.viewCompanyPciDocError = action.payload.message;
    },
  },
});

export const {
  getProfiles,
  setProfiles,
  setProfilesError,
  getCompanies,
  setCompanies,
  setCompaniesError,
  getCompanyDetail,
  setCompanyDetail,
  setCompanyDetailError,
  clearCompanyDetail,
  getExportBankFiles,
  setExportBankFiles,
  setExportBankFilesError,
  getUpdateCompanyProductConfigs,
  setUpdateCompanyProductConfigs,
  setUpdateCompanyProductConfigsError,
  submitOnboardMerchant,
  setOnboardMerchantData,
  setOnboardMerchantError,
  clearAdminCompanyFlags,
  exportCompanyPciDoc,
  setExportCompanyPciDoc,
  setExportCompanyPciDocError,
  viewCompanyPciDoc,
  setViewCompanyPciDoc,
  setViewCompanyPciDocError,
} = adminSlice.actions;

export default adminSlice.reducer;

export const selectAdminState = (root: RootState) => root.adminState;
export const selectProfileHistory = (state: RootState) => state.adminState.ProfileHistory;
export const selectProfileHistoryCurrentPage = (state: RootState) => state.adminState.ProfileDataPages;
export const selectCompanyHistory = (state: RootState) => state.adminState.CompanyHistory;
export const selectCompanyHistoryCurrentPage = (state: RootState) => state.adminState.CompanyDataPages;
